import React, { useMemo, useState } from 'react';
import { Box, Popover, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';

import { useLocalizationStyles } from './styles';
import { ILanguageOption } from './types';

interface AddLanguagePopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  availableLanguages: ILanguageOption[];
  onPick: (code: string) => void;
}

const matchesQuery = (option: ILanguageOption, query: string): boolean => {
  if (!query) {
    return true;
  }
  const lower = query.toLowerCase();
  return (
    option.name.toLowerCase().includes(lower) ||
    option.code.toLowerCase().includes(lower)
  );
};

const AddLanguagePopover = ({
  anchorEl,
  open,
  onClose,
  availableLanguages,
  onPick,
}: AddLanguagePopoverProps) => {
  const { t: translate } = useTranslation();
  const styles = useLocalizationStyles();

  const [query, setQuery] = useState('');

  const filtered = useMemo(
    () => availableLanguages.filter((lang) => matchesQuery(lang, query)),
    [availableLanguages, query],
  );

  const handleExited = () => {
    setQuery('');
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      TransitionProps={{ onExited: handleExited }}
      PaperProps={{ sx: styles.popoverPaper }}
    >
      <Box sx={styles.popoverSearch}>
        <SearchIcon sx={styles.popoverSearchIcon} />
        <TextField
          autoFocus
          variant="outlined"
          size="small"
          fullWidth
          placeholder={translate(
            'editorMode.searchLanguage',
            'Search language…',
          )}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          sx={styles.popoverSearchInput}
        />
      </Box>
      <Box sx={styles.popoverList}>
        {filtered.length === 0 ? (
          <Typography sx={styles.popoverEmpty}>
            {translate('editorMode.noLanguagesFound', 'No languages found')}
          </Typography>
        ) : (
          filtered.map((lang) => (
            <Box
              key={lang.code}
              sx={styles.popoverItem}
              onClick={() => onPick(lang.code)}
            >
              <Box sx={styles.popoverItemDot} />
              <Typography sx={styles.popoverItemName}>{lang.name}</Typography>
              <Typography sx={styles.popoverItemCode}>{lang.code}</Typography>
            </Box>
          ))
        )}
      </Box>
    </Popover>
  );
};

export default AddLanguagePopover;
